import React, { useCallback, useEffect, useState } from 'react';
import { Form } from 'antd';
import {
  CustomButton,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMaskInput,
  CustomModal,
  errorDialog,
  successDialog,
  Text,
  useText,
} from '@src/components';
import modalClose from '@src/assets/icons/icon-close.svg';
import modalWarning from '@src/assets/icons/icon-modal-warning.svg';
import '@src/styles/manageMyCards/eacContactUpdateModal.scss';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  cardGetPagedList,
  employeesUpdateList,
} from '@src/store/slice/manageMyCardsSlice';
import { formMailRegex, formPhoneRegex } from '@src/utils/formRule';
import { buildMobilePaymentDto } from './activeCardList/buildMobilePaymentDto';
import { buildEmployeeUpdateRequestDto } from './activeCardList/buildEmployeeUpdateRequestDto';

const isValidEacGsm = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  const nationalNumber = digits.startsWith('90') ? digits.slice(2) : digits;
  return !digits || /^5\d{9}$/.test(nationalNumber);
};

const isValidEacEmail = (value) =>
  !String(value ?? '').trim() || /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(String(value).trim());

const EacContactUpdateModal = ({
  cards,
  modalVisible,
  handleModalVisible,
  onContactSaved,
  vouId,
}) => {
  const [form] = Form.useForm();
  const [hasContactValues, setHasContactValues] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();
  const { filterObject } = useSelector((state) => state?.manageMyCards);
  const saveErrorMessage = useText('eacContactSaveError');
  const transactionSuccessMessage = useText('yourTransactionHasBeenSuccessfullyCompleted');
  const gsmDuplicateMessage = useText('eacGsmAlreadyUsed');
  const emailDuplicateMessage = useText('eacEmailAlreadyUsed');
  const eacContactTitle = useText('eacContactRequiredTitle');
  const eacContactDescription = useText('eacContactRequiredDescription');
  const emailPlaceholder = useText('emailText');
  const cancelText = useText('giveUp');
  const saveText = useText('saveBtn');

  useEffect(() => {
    if (!modalVisible) return;

    form.resetFields();
    form.setFieldsValue({
      contacts: cards.map(() => ({ gsm: '', email: '' })),
    });
    setHasContactValues(false);
  }, [cards, form, modalVisible]);

  const handleClose = useCallback(() => {
    form.resetFields();
    setHasContactValues(false);
    handleModalVisible(false);
  }, [form, handleModalVisible]);

  const handleSave = useCallback(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      if (validationError?.errorFields) return;
      throw validationError;
    }

    const updatedCards = cards.map((card, index) => ({
      ...card,
      gsm: values.contacts[index].gsm,
      email: values.contacts[index].email?.trim(),
    }));

    setIsSaving(true);
    try {
      for (let index = 0; index < cards.length; index += 1) {
        const card = cards[index];
        const contact = values.contacts[index];
        const updateValues = {
          ...card,
          gsm: contact.gsm,
          email: contact.email?.trim(),
        };
        const action = await dispatch(
          employeesUpdateList({
            customerId: card.customerId,
            vouId,
            requestDto: buildEmployeeUpdateRequestDto({
              record: card,
              values: updateValues,
              isAddressUpdate: false,
            }),
            mobilePaymentUpdateRequestDto: buildMobilePaymentDto({
              record: card,
              values: updateValues,
              vouId,
            }),
          }),
        );

        const response = action?.payload;
        const failed =
          !employeesUpdateList.fulfilled.match(action) ||
          response?.isSuccess === false ||
          response?.isFailure;
        if (failed) {
          const backendMessage =
            response?.message || response?.error?.message || response?.errorMessage || '';
          const errorDescription = `${backendMessage} ${response?.error?.code || response?.code || ''}`
            .toLocaleLowerCase();
          const isDuplicateError =
            /duplicate|already assigned|already exists|belongs to|different|farklı|başka bir kullanıcı|başka bir kart|aittir|kayıtlı|tanımlı|mükerrer/.test(
              errorDescription,
            );
          const isGsmError =
            /gsm|phone|telefon|mobil/.test(errorDescription) ||
            (Boolean(contact.gsm) && !contact.email);
          const isEmailError = /email|e-mail|mail|e-posta/.test(errorDescription);

          if (isDuplicateError && isGsmError && contact.gsm) {
            form.setFields([
              { name: ['contacts', index, 'gsm'], errors: [gsmDuplicateMessage] },
            ]);
          } else if (isDuplicateError && isEmailError && contact.email) {
            form.setFields([
              { name: ['contacts', index, 'email'], errors: [emailDuplicateMessage] },
            ]);
          } else {
            errorDialog({
              title: <Text t="error" />,
              message: backendMessage || saveErrorMessage,
            });
          }
          await dispatch(cardGetPagedList({ body: { vouId }, ...filterObject }));
          return;
        }
      }

      successDialog({
        title: <Text t="successfullySent" />,
        message: transactionSuccessMessage,
        onOk: async () => {
          handleClose();
          await dispatch(cardGetPagedList({ body: { vouId }, ...filterObject }));
          await onContactSaved(updatedCards);
        },
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    cards,
    dispatch,
    emailDuplicateMessage,
    filterObject,
    form,
    gsmDuplicateMessage,
    handleClose,
    onContactSaved,
    saveErrorMessage,
    transactionSuccessMessage,
    vouId,
  ]);

  return (
    <CustomModal
      className="eac-contact-update-modal"
      width={1024}
      maskClosable={false}
      footer={false}
      title={
        <span className="eac-contact-modal-title">
          <CustomImage src={modalWarning} alt="" />
          <Text t="warning2" />
        </span>
      }
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={<CustomImage src={modalClose} />}
    >
      <div className="eac-contact-modal-content">
        <h2>{eacContactTitle}</h2>
        <p>{eacContactDescription}</p>

        <Form
          form={form}
          name="eacContactUpdateForm"
          initialValues={{ contacts: cards.map(() => ({ gsm: '', email: '' })) }}
          onValuesChange={(_, allValues) => {
            setHasContactValues(
              cards.every((_, index) => {
                const contact = allValues?.contacts?.[index];
                const gsm = String(contact?.gsm ?? '').trim();
                const email = String(contact?.email ?? '').trim();
                return (
                  (gsm || email) &&
                  isValidEacGsm(gsm) &&
                  isValidEacEmail(email)
                );
              }),
            );
          }}
        >
          <div className="eac-contact-table-scroll">
            <div className="eac-contact-table">
              <div className="eac-contact-table-header">
                <span>#</span>
                <span><Text t="nameUpperCase" /></span>
                <span><Text t="surnameUpperCase" /></span>
                <span><Text t="referenceNoUpperCase" /></span>
                <span><Text t="gsmNumberUpperCase" /></span>
                <span />
                <span><Text t="emailUpperCase" /></span>
                <span><Text t="deliveryAddressUpperCase" /></span>
              </div>
              {cards.map((card, index) => (
                <div className="eac-contact-table-row" key={card?.id ?? card?.cardNumber ?? index}>
                  <span>{card?.rowNumber ?? index + 1}</span>
                  <span>{card?.name}</span>
                  <span>{card?.surname}</span>
                  <span>{card?.refNo}</span>
                  <CustomFormItem
                    name={['contacts', index, 'gsm']}
                    rules={[
                      {
                        validator: (_, value) => (value ? formPhoneRegex(_, value) : Promise.resolve()),
                        message: <Text t="eacGsmInvalid" />,
                      },
                    ]}
                  >
                    <CustomMaskInput
                      id={`eac-contact-gsm-${card?.id ?? index}`}
                      mask={String.raw`+\90 (999) 999 99 99`}
                    >
                      <CustomInput
                        id={`eac-contact-gsm-input-${card?.id ?? index}`}
                        height={36}
                        placeholder="+90 (___) ___ __ __"
                      />
                    </CustomMaskInput>
                  </CustomFormItem>
                  <span className="eac-contact-or"><Text t="or" /></span>
                  <CustomFormItem
                    name={['contacts', index, 'email']}
                    rules={[
                      { validator: formMailRegex, message: <Text t="eacEmailInvalid" /> },
                    ]}
                  >
                    <CustomInput
                      id={`eac-contact-email-${card?.id ?? index}`}
                      height={36}
                      placeholder={emailPlaceholder}
                    />
                  </CustomFormItem>
                  <span>{card?.addressName}</span>
                </div>
              ))}
            </div>
          </div>
        </Form>

        <div className="eac-contact-modal-actions">
          <CustomButton className="cancel-btn" onClick={handleClose} disabled={isSaving}>
            {cancelText}
          </CustomButton>
          <CustomButton
            className="save-btn"
            onClick={handleSave}
            disabled={!hasContactValues || isSaving}
            loading={isSaving}
          >
            {saveText}
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

EacContactUpdateModal.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.object).isRequired,
  modalVisible: PropTypes.bool.isRequired,
  handleModalVisible: PropTypes.func.isRequired,
  onContactSaved: PropTypes.func.isRequired,
  vouId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default EacContactUpdateModal;
